import { defineStore } from 'pinia';
import { ref } from 'vue';

import { withDefaults } from '@/utils/defaults';

export type NotificationType = 'success' | 'error' | 'info';

export type Notification = {
  id: string;
  message: string;
  type: NotificationType;
};

export const useNotificationsStore = defineStore('notifications', () => {
  const notifications = ref<Array<Notification>>([]);

  /**
   * Add a notification to the display
   *
   * @param args - The notification arguments
   * @param args.message - The notification message
   * @param args.type - The notification type (success, error, info)
   */
  const add = (args: { message: string; type?: NotificationType | null }) => {
    const { message, type } = withDefaults(args, { type: 'info' });
    const id = `notification-${Date.now()}-${Math.random()}`;
    // type is guaranteed to be NotificationType after withDefaults applies the default
    const notification: Notification = {
      id,
      message,
      type: type as NotificationType,
    };

    notifications.value.push(notification);

    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      remove(id);
    }, 5000);
  };

  /**
   * Remove a notification by ID
   *
   * @param id - The notification ID
   */
  const remove = (id: string) => {
    const index = notifications.value.findIndex((n) => n.id === id);
    if (index !== -1) {
      notifications.value.splice(index, 1);
    }
  };

  return {
    notifications,
    add,
    remove,
  };
});
